import $ from 'jquery';
import { mapDiff } from '../../../util/data.js';

/**
 * @typedef {{ [key: string]: string | string[]; }} ChangeHandlerValues
 * @typedef {( 'disable' | 'hide' )[]} ChangeHandlerActions
 * @typedef {{
 * actions?: ChangeHandlerActions;
 * values?: ChangeHandlerValues;
 * affectParentRows?: boolean;
 * }} ChangeHandlerOptions
 */

/**
 * @param {JQuery} $elem
 */
const getHandlerNamespace = ($elem) => {
	return `widget-change-toggle-${$elem.attr('id')}`;
};

/**
 * @param {string | HTMLElement | JQuery} element
 * @param {ChangeHandlerOptions} opt
 */
export const formWidgetsChangeHandler = (element, opt = {}) => {

	/** @type {JQuery} */ // @ts-ignore
	const $widget = element.jquery ? element : $(element);
	const widget = $widget[0];

	const {
		actions = ['disable', 'hide'],
		values = [],
		// triggers = [],
		affectParentRows = false, // This is only for the shop v4 form
	} = opt;

	if (values.length === 0) {
		console.warn('formWidgetsChangeHandler: Widget has no values to handle', widget);
		return;
	}

	if (actions.length === 0) {
		console.warn('formWidgetsChangeHandler: Widget has no actions to handle', widget);
		return;
	}

	// const tLen = triggers.length;
	const canHide = actions.includes('hide');
	const canDisable = actions.includes('disable');
	const isCompound = $widget.is('input, select, textarea') === false;
	const isCheckboxList = $widget.is('[data-widget-checkbox-list-options]') === true;
	const isSelect = widget.tagName === 'SELECT';
	const rowSelector = mpro.config.ui.form_row_selector;
	const columnSelector = mpro.config.ui.form_column_selector;
	const sectionSelector = mpro.config.ui.form_section_selector;

	// if (tLen === 0) {
	// 	console.warn('formWidgetsChangeHandler: Widget has no triggers to handle', widget);
	// 	return;
	// }
	// else if ((tLen === 1) && (triggers[0] !== widget.id)) {
	// 	console.warn('formWidgetsChangeHandler: Widget trigger must be self', widget, triggers);
	// 	return;
	// }

	// console.log('formWidgetsHandleChangeToggle (widgetId: %o, isCompound: %o, actions: %o, values: %o)', widget.id, isCompound, actions, values);

	// Build the list with all toggleable widget ids
	const toggleableWidgetIds = new Set();
	for (const ids of Object.values(values)) {
		if (ids) {
			if (Array.isArray(ids)) {
				for (const id of ids) {
					toggleableWidgetIds.add(id);
				}
			}
			else {
				toggleableWidgetIds.add(ids);
			}
		}
	}

	// Make sure input[type="hidden"] is excluded from row grabbing (it does not usually have a row)
	// Note: this breaks other custom widgets (the date-selector for example, which has a hidden input as the main submit widget)
	// const $widgetRow = $widget.is('input[type="hidden"]') ? $() : $widget.closest(rowSelector);
	// const $widgetRow = $widget.closest(rowSelector);
	// const widgetRow = $widgetRow[0];

	// The main change handler
	const mainChangeHandler = () => {

		const value = isCheckboxList
			// @ts-ignore
			? $widget.find(':checked').map((i, /** @type {HTMLInputElement} */ el) => {
				if (el.name.endsWith('[]')) {
					return el.value;
				}

				const matches = el.name.match(/\[([^\]]*)][^\]]*$/);
				return matches[1];

			}).get()
			: (
				isCompound
					// @ts-ignore
					? $widget.find(':checked').map((i, el) => el.value).get()
					// @ts-ignore
					: (isSelect ? $widget.val() : widget.checked.toString())
			);

		const arrayValue = Array.isArray(value) ? value : [value];
		const mainWidgetIsToggledOff = widget.dataset.toggleHandlerState === 'off';

		// console.log('change (widgetId: %o, value: %o)', widget.id, value);

		for (const toggleableWidgetId of toggleableWidgetIds) {

			// Find the toggleable widget
			const $toggleableWidget = $(`#${toggleableWidgetId}`);
			// Skip if the widget is not found or if it's the same widget
			if ($toggleableWidget.length === 0 || $toggleableWidget[0] === widget) {
				continue;
			}

			const toggleableWidget = $toggleableWidget[0];

			// Ignore some widgets (data-readonly-data, data-disabled-reason)
			// @ts-ignore
			if (toggleableWidget.dataset.readonlyData || (toggleableWidget.disabled && toggleableWidget.dataset.disabledReason)) {
				// console.log('formWidgetsChangeHandler: Ignoring widget (id: %o) because it is readonly/disabled', toggleableWidget.id);
				continue;
			}

			// Create the new state object with all available action set to off
			const newState = new Map();
			if (canHide) {
				newState.set('hidden', true);
			}
			if (canDisable) {
				newState.set('disabled', true);
			}
			let toggleableIsActive = false;

			// The master widget is currently enabled/visible
			if (mainWidgetIsToggledOff !== true) {
				for (const value of arrayValue) {
					// If the value is found in the values map, then the widget will be set as on
					if (values[value]?.includes?.(toggleableWidgetId)) {
						if (canHide) {
							newState.set('hidden', false);
						}
						if (canDisable) {
							newState.set('disabled', false);
						}
						toggleableIsActive = true;
						break;
					}
				}
			}

			// Get current row
			const $toggleableWidgetRow = $toggleableWidget.closest(rowSelector);
			// const toggleableWidgetRow = $toggleableWidgetRow[0];

			// Get current widget toggle state
			const currentToggledStateOff = toggleableWidget.dataset.toggleHandlerState === 'off';

			// Get current widget state
			const currentState = new Map();

			if (canHide) {
				currentState.set('hidden', currentToggledStateOff);
			}
			if (canDisable) {
				currentState.set('disabled', currentToggledStateOff);
			}

			// Get only the changed state
			const diffState = mapDiff(newState, currentState);

			// console.log('formWidgetsHandleChangeToggle (widgetId: %o, toggleableWidgetId: %o, toggleableIsActive: %o, currentToggledStateOff: %o, currentState: %o, newState: %o, diffState: %o)', widget.id, toggleableWidgetId, toggleableIsActive, currentToggledStateOff, currentState, newState, diffState);

			// Skip if the states are equal
			if (diffState.size === 0) {
				continue;
			}

			// console.debug('ToggleChangeHandler: changing widget (toggler: %o / toggled: %o) from %o to %o', widget.id, toggleableWidgetId, currentState, newState);

			// Close tooltips/popovers
			mpro.closeSimpleOverlays();

			const $toggleableSameRowParents = (affectParentRows === true) ? $toggleableWidgetRow.parents(rowSelector) : $();

			// Toggle the widget hidden state
			if (diffState.has('hidden')) {

				const hiddenState = diffState.get('hidden');
				let $hideContainers = $toggleableWidgetRow;

				if ($toggleableSameRowParents.length > 0) {
					$hideContainers = $hideContainers.add($toggleableSameRowParents);
				}

				// Hide
				if (hiddenState === true) {

					// See if there are any other rows on the same level
					// ? Note: look for the display:none style, do NOT use the ':hidden' selector as it could catch false positives
					const $otherRows = $toggleableWidgetRow.siblings(rowSelector).not((_, el) => el.style.display === 'none');

					if ($otherRows.length === 0) {
						const $rowSection = $toggleableWidgetRow.closest(sectionSelector);

						if ($rowSection.length > 0) {
							$hideContainers = $hideContainers.add($rowSection);
						}

						// If there are no other rows in the section, hide the column
						// ? Note: look for the display:none style, do NOT use the ':hidden' selector as it could catch false positives
						const $otherSections = $rowSection.siblings(sectionSelector).not((_, el) => el.style.display === 'none');

						if ($otherSections.length === 0) {
							const $column = $rowSection.closest(columnSelector);

							if ($column.length > 0) {
								$hideContainers = $hideContainers.add($column);
							}
						}
					}
				}
				// Show
				else {

					// ? Note: look for the display:none style, do NOT use the ':hidden' selector as it could catch false positives
					const $rowSection = $toggleableWidgetRow.closest(sectionSelector).filter((_, el) => el.style.display === 'none');

					if ($rowSection.length > 0) {
						$hideContainers = $hideContainers.add($rowSection);

						// ? Note: look for the display:none style, do NOT use the ':hidden' selector as it could catch false positives
						const $column = $rowSection.closest(columnSelector).filter((_, el) => el.style.display === 'none');

						if ($column.length > 0) {
							$hideContainers = $hideContainers.add($column);
						}
					}

					// Do nothing if the widget was explicitly hidden by the parent logic
					$hideContainers = $hideContainers.filter((_, v) => {
						return ((v.dataset.hidden !== '') && (v.dataset.hidden !== 'true'));
					});
				}

				// console.log('formWidgetsChangeHandler: (hiddenState: %o) $hideContainers', hiddenState, $hideContainers);


				$hideContainers.finish()[hiddenState ? 'hide' : 'show']();
			}

			// Toggle the widget disabled state
			if (diffState.has('disabled')) {

				const disabledState = diffState.get('disabled');
				let $toggleableFormElements = $toggleableWidget.is('input, select, textarea, button') ? $toggleableWidget : $toggleableWidget.find('input, select, textarea, button');

				// Do nothing if the widget was explicitly disabled by the parent logic
				if (disabledState === false) {
					$toggleableFormElements = $toggleableFormElements.filter((_, v) => {
						return ((v.dataset.disabled !== '') && (v.dataset.disabled !== 'true'));
					});
				}

				if ($toggleableFormElements.length > 0) {

					// Change the disabled state on the form elements
					$toggleableFormElements.prop('disabled', disabledState);

					// Add the disabled class on the row
					$toggleableWidgetRow[disabledState ? 'addClass' : 'removeClass']('disabled');

					// Visually disable all parent rows
					if ($toggleableSameRowParents.length > 0) {
						$toggleableSameRowParents[disabledState ? 'addClass' : 'removeClass']('disabled');
					}

					// Trigger custom disable event on the form element
					$toggleableWidget.trigger('disable.bind-change-action', {
						disabled: disabledState,
						$from: $widget,
						$toRow: $toggleableWidgetRow,
						$toElements: $toggleableFormElements,
					});
				}
			}

			// Update the toggle handler state
			toggleableWidget.dataset.toggleHandlerState = toggleableIsActive ? 'on' : 'off';

			// Trigger the change handler on the toggleable widget (if it's a toggle handler)
			$toggleableWidget.triggerHandler(`change.${getHandlerNamespace($toggleableWidget)}`);
		}
	};

	// Add the change handler
	$widget.on(`change.${getHandlerNamespace($widget)}`, mainChangeHandler);

	// Must trigger the change handler on the widget to initialize the toggleable widgets state correctly (the view might decide to disable/enable some widgets on load in some remote cases)
	mainChangeHandler();
};